/**
 * Frontend Sentry initialisation (C20).
 *
 * - DSN comes from VITE_SENTRY_DSN (injected at build/deploy time; ultimately
 *   sourced from Secret Manager). When unset, init is a no-op.
 * - release tag = git SHA (VITE_GIT_SHA), wired to source-map upload in CI so
 *   stack traces are symbolicated.
 * - We do not send default PII; user context, if set, must be an opaque UUID.
 */

/** Returns the release tag (git SHA) for Sentry events, if available. */
export function frontendRelease(): string | undefined {
  return import.meta.env.VITE_GIT_SHA as string | undefined;
}

/**
 * Initialises Sentry in the browser. Returns true when initialised, false when
 * skipped (no DSN). Dynamically imports the SDK so it is only pulled into the
 * bundle path when actually used.
 */
export async function initSentry(): Promise<boolean> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return false;

  const Sentry = await import("@sentry/browser");
  Sentry.init({
    dsn,
    release: frontendRelease(),
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // PII guard: do not auto-attach IP addresses / request data.
    sendDefaultPii: false,
  });
  return true;
}

/**
 * Associates the current session with an opaque user id for Sentry events.
 * Pass ONLY an opaque UUID — never an email or other PII.
 */
export async function setSentryUser(userId: string | undefined): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  const Sentry = await import("@sentry/browser");
  Sentry.setUser(userId ? { id: userId } : null);
}
